/**
 * Sync (drain) outbox - wysyłka kolejki po odzyskaniu sieci.
 */

import {
  deleteEntry,
  getAllPending,
  updateStatus,
  getAllNeedingPassword,
} from "./outbox";
import { offlineFetch } from "./offlineFetch";
import { isAdminPasswordRejection } from "./sanitize";
import {
  forgetOutboxPassword,
  peekOutboxPassword,
  rememberOutboxPassword,
} from "./ephemeralPasswords";
import type { SyncResult, OutboxEntry } from "./types";

let drainInFlight: Promise<SyncResult> | null = null;

export async function drainOutbox(): Promise<SyncResult> {
  if (drainInFlight) {
    return drainInFlight;
  }
  drainInFlight = runDrain().finally(() => {
    drainInFlight = null;
  });
  return drainInFlight;
}

async function runDrain(): Promise<SyncResult> {
  if (!navigator.onLine) {
    return emptyResult();
  }

  const pending = await getAllPending();
  if (pending.length === 0) {
    return emptyResult();
  }

  const result: SyncResult = {
    synced: 0,
    failed: 0,
    needs_password: 0,
    failed_entries: [],
  };

  for (const entry of pending) {
    await updateStatus(entry.id, "syncing");
    const outcome = await syncEntry(entry);

    if (outcome.success) {
      result.synced += 1;
      forgetOutboxPassword(entry.client_id);
      await deleteEntry(entry.id);
    } else if (outcome.needsPassword) {
      result.needs_password += 1;
      await updateStatus(entry.id, "needs_password", outcome.error);
    } else {
      result.failed += 1;
      result.failed_entries.push({
        id: entry.id,
        path: entry.path,
        error: outcome.error || "Unknown error",
      });
      await updateStatus(entry.id, "failed", outcome.error);
    }
  }

  return result;
}

function emptyResult(): SyncResult {
  return { synced: 0, failed: 0, needs_password: 0, failed_entries: [] };
}

type SyncOutcome = {
  success: boolean;
  needsPassword?: boolean;
  error?: string;
  data?: unknown;
};

async function syncEntry(entry: OutboxEntry, passwordOverride?: string): Promise<SyncOutcome> {
  const body: Record<string, unknown> = { ...entry.body };
  const password = passwordOverride || peekOutboxPassword(entry.client_id);
  if (password) {
    body.password = password;
  }

  try {
    const response = await offlineFetch(entry.method, entry.path, body);

    if (response.ok) {
      return { success: true, data: response.data };
    }

    if (isAdminPasswordRejection(response.status, response.error)) {
      return {
        success: false,
        needsPassword: true,
        error: stringifyError(response.error, `HTTP ${response.status}`),
      };
    }

    if (response.status === 404 || response.status === 409) {
      return {
        success: false,
        error: stringifyError(response.error, `HTTP ${response.status}`),
      };
    }

    return {
      success: false,
      error: stringifyError(response.error, `HTTP ${response.status}`),
    };
  } catch (error) {
    return {
      success: false,
      error: String(error),
    };
  }
}

function stringifyError(error: unknown, fallback: string): string {
  if (typeof error === "string" && error.trim()) {
    return error;
  }
  if (error && typeof error === "object" && "detail" in error) {
    const detail = (error as { detail: unknown }).detail;
    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
  }
  return fallback;
}

export async function retryEntry(
  entryId: number,
  newPassword?: string,
): Promise<{ success: boolean; error?: string }> {
  const needsPassword = await getAllNeedingPassword();
  const pending = await getAllPending();
  const entry = [...needsPassword, ...pending].find((e) => e.id === entryId);

  if (!entry) {
    return { success: false, error: "Nie znaleziono wpisu w kolejce." };
  }

  if (newPassword) {
    rememberOutboxPassword(entry.client_id, newPassword);
  }

  await updateStatus(entry.id, "pending");
  const outcome = await syncEntry(entry, newPassword);

  if (outcome.success) {
    forgetOutboxPassword(entry.client_id);
    await deleteEntry(entry.id);
    return { success: true };
  }

  if (outcome.needsPassword) {
    await updateStatus(entry.id, "needs_password", outcome.error);
    return { success: false, error: outcome.error };
  }

  await updateStatus(entry.id, "failed", outcome.error);
  return { success: false, error: outcome.error };
}

export async function retryNeedsPassword(password: string): Promise<SyncResult> {
  const entries = await getAllNeedingPassword();
  for (const entry of entries) {
    rememberOutboxPassword(entry.client_id, password);
    await updateStatus(entry.id, "pending");
  }
  return drainOutbox();
}

export async function discardFailedEntry(entryId: number): Promise<void> {
  await deleteEntry(entryId);
}
